import { useResetScrollToTop } from '@zuto/utilities/src/Hooks/useResetScrollToTop'
import React, { useContext } from 'react'
import { styled, Typography } from '@mui/material'
import DataRow from '@zuto/atoms/src/Mui/DataRow/DataRow'
import Card from '@zuto/atoms/src/Mui/Card/Card'
import DataSection from '@zuto/molecules/src/Mui/DataSection/DataSection'
import $console from '@zuto/utilities/src/$console'
import ZutoAnalyticsHelper from '@zuto-internal/zuto-analytics-helper'
import { v4 as uuidv4 } from 'uuid'
import { RequiredInfo } from '@zuto/atoms/src/RequiredInfo/RequiredInfo'
import { pluralize } from '../../utils/utils'
import { PrefillOverviewProps } from '../ApplicationForm/types'
import withFade from '../HOC/withFade'
import { hasLengthOfTime, lengthOfTimeMonths, logContentClicked } from '../../utils/PrefillReviewPageApplicationForm-utils'
import { PrefillSectionType, ApplicationFormContext } from '../ApplicationForm/ApplicationFormContext'
import { fireReactGAEvent } from '../Questions/utils'
import { PrefillReviewPageApplicationFormContext } from '../ApplicationForm/PrefillReviewPageApplicationFormContext'
import { EmploymentHistoryEntry } from '../Questions/CardlessEmploymentHistoryQuestion/EmploymentHistoryContext'
import { AddressHistoryEntry } from '../Questions/CardlessAddressHistoryQuestion/AddressHistoryContext'
import { employerDetailsNotRequiredFor } from '../Questions/CardlessEmploymentHistoryQuestion/EmploymentStatus/EmploymentStatusOptions'

const DataContainer = styled('div')({
  '& .data-row': {
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: '15px',
    '&.last-row': {
      marginBottom: 0
    },
    '&:first-of-type': {
      alignItems: 'center'
    },
    '& .heading, & .content': {
      display: 'flex',
      flexDirection: 'column',
      flexBasis: '50%',
      wordBreak: 'break-word',
      '&[id="email-address-answer"]': {
        p: {
          '&:first-letter': {
            textTransform: 'none'
          }
        }
      },
      p: {
        margin: 0,
        '&:first-letter': {
          textTransform: 'uppercase'
        },
        span: {
          paddingLeft: '3px'
        }
      }
    },
    '& .content': {
      alignItems: 'flex-end',
      textAlign: 'right'
    }
  }
})

const PrefillDataOverview = ({
  prefillData
}: PrefillOverviewProps): JSX.Element => {
  useResetScrollToTop()
  const {
    setShowReviewPage, experimentName, setRequiredEntryId, setHasNavigatedFromRequiredClick
  } = useContext(PrefillReviewPageApplicationFormContext)
  const appFormContext = useContext(ApplicationFormContext)
  const { featureSwitches } = useContext(ApplicationFormContext)
  const { reviewPageV3 } = featureSwitches ?? {}
  const zutoAnalyticsHelper = new ZutoAnalyticsHelper()

  const addressHistory = prefillData?.addressHistory?.entries || {}
  const employmentHistory = prefillData?.employmentHistory?.entries || {}
  const totalEmploymentMonths = lengthOfTimeMonths(employmentHistory as Record<string, EmploymentHistoryEntry>)
  const isEmploymentLengthLessThan24Months = totalEmploymentMonths < 24
  const totalAddressMonths = lengthOfTimeMonths(addressHistory as Record<string, AddressHistoryEntry>)
  const isAddressLengthLessThan36Months = totalAddressMonths < 36

  const personalDetailsSectionId = 'edit-personal-details'
  const vehicleDetailsSectionId = 'edit-vehicle-details'
  const addressDetailsSectionId = 'edit-address-details'
  const employmentSectionId = 'edit-employment-details'

  const removeUndefinedFromYearOrMonthValue = (value: string) => {
    if ((value && value.includes('undefined') && (value.includes('Months') || value.includes('Years')))
      || (value && value.includes('undefined') && (value.includes('Month') || value.includes('Year')))) {
      return (
        value.replace('undefined', '')
      )
    }
    return value
  }

  const handleEditSection = (sectionId: PrefillSectionType) => {
    setShowReviewPage(false)
    appFormContext.dispatch(
      {
        type: 'EDIT_PREFILL_SECTION',
        value: sectionId
      }
    )
    try {
      zutoAnalyticsHelper.sendCustomEvent({
        eventType: 'Prefill Review Page - Edit Section Clicked',
        eventCategory: 'Prefill',
        eventAction: `Edit Section Clicked - ${sectionId}`,
        experiment: experimentName
      })
      fireReactGAEvent({
        category: 'Application Form',
        action: 'Edit Section',
        label: `Edit Section Clicked - ${sectionId}`,
        nonInteraction: false
      })

      if (reviewPageV3) {
        setHasNavigatedFromRequiredClick(false)
        setRequiredEntryId('')
      }
    } catch (error) {
      $console.error('zutoAnalyticsHelper.sendCustomEvent error', error)
    }
  }

  const handleRequiredClick = (requiredEntryId: string, sectionId: PrefillSectionType) => {
    setRequiredEntryId(requiredEntryId)
    setHasNavigatedFromRequiredClick(true)
    appFormContext.dispatch(
      {
        type: 'EDIT_PREFILL_SECTION',
        value: sectionId
      }
    )
  }

  const renderPersonalDetailsSection = () => (
    <DataSection
      title="Personal Details"
      onClick={() => handleEditSection(personalDetailsSectionId)}
    >
      <DataRow
        field="Full Name"
        value={`${prefillData.personalDetails?.title} ${prefillData.personalDetails?.firstName} ${prefillData.personalDetails?.surname}`}
        infoId="full-name"
        onClick={reviewPageV3 ? () => handleRequiredClick('required-full-name', personalDetailsSectionId) : undefined}
      />
      <DataRow
        field="Marital Status"
        value={prefillData.maritalStatus}
        infoId="marital-status"
        onClick={reviewPageV3 ? () => handleRequiredClick('required-marital-status', personalDetailsSectionId) : undefined}
      />
      <DataRow
        field="Date of Birth"
        value={`${prefillData.dateOfBirth?.day}/${prefillData.dateOfBirth?.month}/${prefillData.dateOfBirth?.year}`}
        infoId="dob"
        onClick={reviewPageV3 ? () => handleRequiredClick('required-dob', personalDetailsSectionId) : undefined}
      />
      <DataRow
        field="Phone Number"
        value={prefillData.personalDetails?.contactNumber}
        infoId="phone-number"
        onClick={reviewPageV3 ? () => handleRequiredClick('required-phone-number', personalDetailsSectionId) : undefined}
      />
      <DataRow
        field="Email Address"
        value={prefillData.personalDetails?.emailAddress}
        infoId="email-address"
        onClick={reviewPageV3 ? () => handleRequiredClick('required-email-address', personalDetailsSectionId) : undefined}
      />
    </DataSection>
  )

  const renderVehicleDetailsSection = () => (
    <DataSection
      title="Vehicle Details"
      onClick={() => handleEditSection(vehicleDetailsSectionId)}
    >
      <DataRow
        field="Vehicle to Finance"
        value={prefillData.vehicleType}
        infoId="vehicle-to-finance"
        onClick={reviewPageV3 ? () => handleRequiredClick('required-vehicle-to-finance', vehicleDetailsSectionId) : undefined}
      />
      <DataRow
        field="Licence Type"
        value={prefillData.drivingLicence}
        infoId="licence-type"
        onClick={reviewPageV3 ? () => handleRequiredClick('required-licence-type', vehicleDetailsSectionId) : undefined}
      />
      <DataRow
        field="Loan Amount"
        value={`${prefillData.loanAmount !== 'Not sure yet' ? `£${prefillData.loanAmount}` : prefillData.loanAmount}`}
        infoId="loan-amount"
        testId="prefill-loan-amount"
        onClick={reviewPageV3 ? () => handleRequiredClick('required-loan-amount', vehicleDetailsSectionId) : undefined}
      />
    </DataSection>
  )

  const renderAddressDetailsSection = () => (
    <DataSection
      title="Address Details"
      onClick={() => handleEditSection(addressDetailsSectionId)}
    >
      {Object.values(addressHistory).map((item, index, array) => {
        const isFirstEntry = index === 0
        const isLastIndex = index === array.length - 1
        const hasSomeDetails = item.addressDetails?.buildingNumber
          || item.addressDetails?.flatNumber
          || item.addressDetails?.buildingName
          || item.addressDetails?.street
          || item.addressDetails?.town
          || item.addressDetails?.postcode
          || item.addressSituation
          || item.lengthOfTime

        const renderAddressDetails = () => (
          <>
            <Typography variant="rowHeading" onClick={() => logContentClicked(experimentName, isFirstEntry ? 'address-name-number' : 'previous-address-name-number')}>
              {item.addressDetails?.flatNumber} {item.addressDetails?.buildingName} {item.addressDetails?.buildingNumber}
            </Typography>
            <Typography variant="rowHeading" onClick={() => logContentClicked(experimentName, isFirstEntry ? 'address-street' : 'previous-address-street')}>{item.addressDetails?.street}</Typography>
            <Typography variant="rowHeading" onClick={() => logContentClicked(experimentName, isFirstEntry ? 'address-town' : 'previous-address-town')}>{item.addressDetails?.town}</Typography>
            <Typography variant="rowHeading" onClick={() => logContentClicked(experimentName, isFirstEntry ? 'address-county' : 'previous-address-county')}>{item.addressDetails?.county}</Typography>
            <Typography variant="rowHeading" onClick={() => logContentClicked(experimentName, isFirstEntry ? 'address-postcode' : 'previous-address-postcode')}>{item.addressDetails?.postcode}</Typography>
          </>
        )

        return (
          <React.Fragment key={uuidv4()}>
            <div className="data-row">
              <div className="heading">
                <Typography variant="rowHeading">
                  {isFirstEntry ? 'Address:' : 'Previous Address:'}
                </Typography>
              </div>
              <div className="content" data-hj-suppress="" id={isFirstEntry ? 'current-address-details-answer' : 'previous-address-details-answer'}>
                {!reviewPageV3 && (
                  ((item.addressDetails?.buildingNumber || item.addressDetails?.flatNumber || item.addressDetails?.buildingName)
                    && item.addressDetails?.street
                    && item.addressDetails?.town
                    && item.addressDetails?.postcode)
                    ? (
                      renderAddressDetails()
                    ) : (
                      <RequiredInfo
                        experimentName={experimentName}
                        testId={isFirstEntry ? 'required-current-address-details' : 'required-previous-address-details'}
                        onClickHandler={reviewPageV3 ? () => handleRequiredClick(`required-address-${index}`, addressDetailsSectionId) : undefined}
                        clickable={reviewPageV3}
                      />
                    )
                )}
                {reviewPageV3 && (
                  ((item.addressDetails?.buildingNumber || item.addressDetails?.flatNumber || item.addressDetails?.buildingName)
                    && item.addressDetails?.street
                    && item.addressDetails?.town
                    && item.addressDetails?.postcode
                    && item.addressSituation
                    && hasLengthOfTime(item.lengthOfTime)) ? (
                    renderAddressDetails()
                  ) : (
                    <>
                      <RequiredInfo
                        experimentName={experimentName}
                        testId={isFirstEntry ? 'required-current-address-details' : 'required-previous-address-details'}
                        onClickHandler={reviewPageV3 ? () => handleRequiredClick(`required-address-${index}`, addressDetailsSectionId) : undefined}
                        clickable={reviewPageV3}
                        text={hasSomeDetails ? 'Incomplete' : 'Required'}
                      />
                      {renderAddressDetails()}
                    </>
                  )
                )}
              </div>
            </div>
            {(reviewPageV3 && (item.addressSituation !== undefined || item.lengthOfTime !== undefined))
              || (!reviewPageV3) ? (
              <>
                <DataRow
                  field="Status"
                  value={item.addressSituation}
                  testId={isFirstEntry ? 'current-residence-status-heading' : 'previous-residence-status-heading'}
                  infoId={
                    isFirstEntry ? 'current-address-status' : 'previous-address-status'
                  }
                  onClick={reviewPageV3 ? () => handleRequiredClick(`required-address-status-${index}`, addressDetailsSectionId) : undefined}
                  hideRequired={reviewPageV3}
                />

                <DataRow
                  field="Time at Residence"
                  value={removeUndefinedFromYearOrMonthValue(`${item.lengthOfTime?.years && item.lengthOfTime.years !== '0'
                    ? `${item.lengthOfTime.years} ${pluralize(
                      'Year',
                      Number(item.lengthOfTime.years)
                    )}` : undefined}
                    ${item.lengthOfTime?.months && item.lengthOfTime.months !== '0'
                      ? `${item.lengthOfTime.months} ${pluralize(
                        'Month',
                        Number(item.lengthOfTime.months)
                      )}` : undefined}`)}
                  testId={isFirstEntry ? 'time-at-current-residence' : 'time-at-previous-residence'}
                  infoId={
                    isFirstEntry ? 'time-at-current-residence' : 'time-at-previous-residence'
                  }
                  onClick={reviewPageV3 ? () => handleRequiredClick(`required-address-length-${index}`, addressDetailsSectionId) : undefined}
                  hideRequired={reviewPageV3}
                />
              </>
            ) : null}
            {(isLastIndex && reviewPageV3
              && isAddressLengthLessThan36Months
              && (prefillData?.addressHistory?.entries[array.length]?.lengthOfTime?.years !== undefined
                || prefillData?.addressHistory?.entries[array.length]?.lengthOfTime?.months !== undefined))
              || (isLastIndex && !reviewPageV3
                && isAddressLengthLessThan36Months
                && item.addressDetails?.postcode !== ''
                && (prefillData?.addressHistory?.entries[1]?.lengthOfTime?.years !== undefined
                  || prefillData?.addressHistory?.entries[1]?.lengthOfTime?.months !== undefined))
              ? (
                <DataRow
                  field="Previous Address"
                  value={undefined}
                  infoId="previous-address-details"
                  onClick={reviewPageV3 ? () => handleRequiredClick(`required-address-${index + 1}`, addressDetailsSectionId) : undefined}
                />
              ) : null}
          </React.Fragment>
        )
      })}
      {/* Fallback for when we are not passed any address details at all */}
      {Object.keys(addressHistory).length === 0
        ? (
          <DataRow
            field="Address"
            value={undefined}
            infoId="current-address-details"
            onClick={reviewPageV3 ? () => handleRequiredClick('required-address-0', addressDetailsSectionId) : undefined}
          />
        )
        : null}
    </DataSection>
  )

  const renderEmploymentDetailsSection = () => (
    <DataSection
      title="Employment"
      onClick={() => handleEditSection(employmentSectionId)}
      hideDivider
    >
      <DataRow
        field="Monthly Income (Net)"
        value={`£${prefillData.monthlyIncome}`}
        infoId="monthly-income"
        onClick={reviewPageV3 ? () => handleRequiredClick('required-monthly-income', employmentSectionId) : undefined}
      />

      {Object.values(employmentHistory).map((item, index, array) => {
        const isFirstEntry = index === 0
        const isLastIndex = index === array.length - 1
        const isSituationInEmployerDetailsNotRequired = item.employmentSituation
          && employerDetailsNotRequiredFor.includes(
            item.employmentSituation.toLowerCase().replace(/\s/g, '')
          )

        const isEmploymentDetailsIncomplete = (!isSituationInEmployerDetailsNotRequired
          && (!item.employerDetails?.companyName
            || !item.employerDetails?.jobTitle))
          || !(item.lengthOfTime?.years || item.lengthOfTime?.months)
          || !item.employmentSituation

        const showRequiredInfo = !isSituationInEmployerDetailsNotRequired
          && (!item.employerDetails?.companyName
            || !item.employerDetails?.jobTitle)
          && !(item.lengthOfTime?.years || item.lengthOfTime?.months)
          && !item.employmentSituation

        const showIncompleteInfo = isEmploymentDetailsIncomplete && !showRequiredInfo

        return (
          <React.Fragment key={uuidv4()}>
            <div className={`data-row ${isLastIndex && !isEmploymentLengthLessThan24Months ? 'last-row' : ''}`}>
              <div className="heading">
                <Typography variant="rowHeading">
                  {isFirstEntry ? 'Employment:' : 'Previous Employment:'}
                </Typography>
              </div>
              <div className="content" data-hj-suppress="" id={isFirstEntry ? 'current-employment-answer' : 'previous-employment-answer'}>
                {isEmploymentDetailsIncomplete ? (
                  <>
                    {showRequiredInfo
                      && (
                        <RequiredInfo
                          experimentName={experimentName}
                          testId={isFirstEntry ? 'required-current-employment' : 'required-previous-employment'}
                          onClickHandler={reviewPageV3 ? () => handleRequiredClick(`required-employment-${index}`, employmentSectionId) : undefined}
                          clickable={reviewPageV3}
                        />
                      )}
                    {showIncompleteInfo
                      && (
                        <RequiredInfo
                          experimentName={experimentName}
                          testId={isFirstEntry ? 'incomplete-current-employment' : 'incomplete-previous-employment'}
                          text="Incomplete"
                          onClickHandler={reviewPageV3 ? () => handleRequiredClick(`incomplete-employment-${index}`, employmentSectionId) : undefined}
                          clickable={reviewPageV3}
                        />
                      )}
                    {item.employerDetails?.companyName && (
                      <Typography variant="rowHeading" onClick={() => logContentClicked(experimentName, isFirstEntry ? 'employment-company-name' : 'previous-employment-company-name')}>{item.employerDetails.companyName}</Typography>
                    )}
                    {item.employerDetails?.jobTitle && (
                      <Typography variant="rowHeading" onClick={() => logContentClicked(experimentName, isFirstEntry ? 'employment-job-title' : 'previous-employment-job-title')}>{item.employerDetails.jobTitle}</Typography>
                    )}
                    <Typography
                      variant="rowHeading"
                      data-testid={isFirstEntry ? 'time-at-current-employment' : 'time-at-previous-employment'}
                      onClick={() => logContentClicked(experimentName, isFirstEntry ? 'employment-length' : 'previous-employment-length')}
                    >
                      {item.lengthOfTime?.years && (
                        <span>
                          <span>{item.lengthOfTime.years}</span>
                          <span>{pluralize('Year', Number(item.lengthOfTime.years))}</span>
                        </span>
                      )}
                      {item.lengthOfTime?.months && (
                        <span>
                          <span>{item.lengthOfTime.months}</span>
                          <span>{pluralize('Month', Number(item.lengthOfTime.months))}</span>
                        </span>
                      )}
                    </Typography>
                    {item.employmentSituation && (
                      <Typography variant="rowHeading" onClick={() => logContentClicked(experimentName, isFirstEntry ? 'employment-situation' : 'previous-employment-situation')}>{item.employmentSituation}</Typography>
                    )}
                  </>
                ) : (
                  <>
                    {item.employerDetails?.companyName && (
                      <Typography variant="rowHeading" onClick={() => logContentClicked(experimentName, isFirstEntry ? 'employment-company-name' : 'previous-employment-company-name')}>{item.employerDetails.companyName}</Typography>
                    )}
                    {item.employerDetails?.jobTitle && (
                      <Typography variant="rowHeading" onClick={() => logContentClicked(experimentName, isFirstEntry ? 'employment-job-title' : 'previous-employment-job-title')}>{item.employerDetails.jobTitle}</Typography>
                    )}
                    <Typography
                      variant="rowHeading"
                      data-testid={isFirstEntry ? 'time-at-current-employment' : 'time-at-previous-employment'}
                      onClick={() => logContentClicked(experimentName, isFirstEntry ? 'employment-length' : 'previous-employment-length')}
                    >
                      {item.lengthOfTime?.years && (
                        <span>
                          <span>{item.lengthOfTime.years}</span>
                          <span>{pluralize('Year', Number(item.lengthOfTime.years))}</span>
                        </span>
                      )}
                      {item.lengthOfTime?.months && (
                        <span>
                          <span>{item.lengthOfTime.months}</span>
                          <span>{pluralize('Month', Number(item.lengthOfTime.months))}</span>
                        </span>
                      )}
                    </Typography>
                    {item.employmentSituation && (
                      <Typography variant="rowHeading" onClick={() => logContentClicked(experimentName, isFirstEntry ? 'employment-situation' : 'previous-employment-situation')}>{item.employmentSituation}</Typography>
                    )}
                  </>
                )}
              </div>
            </div>
            {isLastIndex
              && isEmploymentLengthLessThan24Months
              && (prefillData?.employmentHistory?.entries[1].lengthOfTime?.years !== undefined
                || prefillData?.employmentHistory?.entries[1].lengthOfTime?.months !== undefined)
              && (item.lengthOfTime?.years || item.lengthOfTime?.months !== undefined) && (
                <DataRow
                  field="Previous Employment"
                  value={undefined}
                  infoId="previous-employment"
                  lastRow
                  onClick={reviewPageV3 ? () => handleRequiredClick(`required-employment-${index + 1}`, employmentSectionId) : undefined}
                />
              )}
          </React.Fragment>
        )
      })}
      {/* Fallback for when we are not passed any employment details at all */}
      {Object.keys(employmentHistory).length === 0
        ? (
          <DataRow
            field="Employment"
            value={undefined}
            infoId="current-employment"
            onClick={reviewPageV3 ? () => handleRequiredClick('required-employment-0', employmentSectionId) : undefined}
          />
        )
        : null}
    </DataSection>
  )

  const renderSections = () => {
    return (
      <Card testId="prefill-data-card">
        {renderPersonalDetailsSection()}
        {renderVehicleDetailsSection()}
        {renderAddressDetailsSection()}
        {renderEmploymentDetailsSection()}
      </Card>
    )
  }

  return (
    <DataContainer>
      {renderSections()}
    </DataContainer>
  )
}

export default withFade(PrefillDataOverview)
